import React from 'react';
import {
    Box, Typography, Chip, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress,
} from '@mui/material';
import {
    OptionTypeLabels, OptionTypeColors,
    FlowSignalTypeLabels, FlowSignalTypeColors,
} from '../../stockTradingEnums';

const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';
const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString() : '-';
const formatDateTime = (val) => val ? new Date(val).toLocaleString() : '-';
const formatNumber = (val) => val != null ? Number(val).toLocaleString() : '-';

const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, fontSize: '1rem' }}>{children}</Typography>
);
const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message || 'No data available'}</Typography></Box>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);

const OptionsTab = ({ data, loading, error }) => {
    if (loading) return <TabLoading />;
    if (error) return <EmptyState message={error} />;
    if (!data) return <EmptyState />;

    const {
        option_contracts = [],
        oi_snapshots = [],
        options_flow = [],
        implied_volatility = [],
    } = data;

    return (
        <Box>
            {/* Options Chain */}
            <SectionTitle>Options Chain</SectionTitle>
            {option_contracts.length === 0 ? <EmptyState message="No options chain data available" /> : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Expiration</TableCell>
                                <TableCell align="right">Strike</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Bid</TableCell>
                                <TableCell align="right">Ask</TableCell>
                                <TableCell align="right">Last</TableCell>
                                <TableCell align="right">Volume</TableCell>
                                <TableCell align="right">Open Interest</TableCell>
                                <TableCell align="right">IV</TableCell>
                                <TableCell align="right">Delta</TableCell>
                                <TableCell align="right">Gamma</TableCell>
                                <TableCell align="right">Theta</TableCell>
                                <TableCell align="right">Vega</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {option_contracts.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{formatDate(row.expiration_date)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.strike_price)}</TableCell>
                                    <TableCell>
                                        {row.option_type ? (
                                            <Chip
                                                label={OptionTypeLabels[row.option_type] || row.option_type}
                                                color={OptionTypeColors[row.option_type] || 'default'}
                                                size="small"
                                            />
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(row.bid)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.ask)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.last_price)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.volume)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.open_interest)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.implied_volatility)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.delta)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.gamma)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.theta)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.vega)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* OI Snapshots */}
            <SectionTitle>Open Interest Snapshots</SectionTitle>
            {oi_snapshots.length === 0 ? <EmptyState message="No OI snapshot data available" /> : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="right">Strike Price</TableCell>
                                <TableCell>Expiration</TableCell>
                                <TableCell>Option Type</TableCell>
                                <TableCell align="right">Open Interest</TableCell>
                                <TableCell align="right">OI Change</TableCell>
                                <TableCell>Snapshot Time</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {oi_snapshots.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell align="right">{formatPrice(row.strike_price)}</TableCell>
                                    <TableCell>{formatDate(row.expiration_date)}</TableCell>
                                    <TableCell>{row.option_type || '-'}</TableCell>
                                    <TableCell align="right">{formatNumber(row.open_interest)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.oi_change)}</TableCell>
                                    <TableCell>{formatDateTime(row.snapshot_timestamp)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Options Flow */}
            <SectionTitle>Options Flow</SectionTitle>
            {options_flow.length === 0 ? <EmptyState message="No options flow data available" /> : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Flow Signal</TableCell>
                                <TableCell>Option Type</TableCell>
                                <TableCell align="right">Strike</TableCell>
                                <TableCell align="right">Volume</TableCell>
                                <TableCell align="right">Premium Total</TableCell>
                                <TableCell>Implied Direction</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {options_flow.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        {row.flow_signal_type ? (
                                            <Chip
                                                label={FlowSignalTypeLabels[row.flow_signal_type] || row.flow_signal_type}
                                                color={FlowSignalTypeColors[row.flow_signal_type] || 'default'}
                                                size="small"
                                            />
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {row.option_type ? (
                                            <Chip
                                                label={OptionTypeLabels[row.option_type] || row.option_type}
                                                color={OptionTypeColors[row.option_type] || 'default'}
                                                size="small"
                                            />
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(row.strike_price)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.volume)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.premium_total)}</TableCell>
                                    <TableCell>{row.implied_direction || '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Implied Volatility */}
            <SectionTitle>Implied Volatility</SectionTitle>
            {implied_volatility.length === 0 ? <EmptyState message="No implied volatility data available" /> : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="right">IV Value</TableCell>
                                <TableCell align="right">IV Percentile</TableCell>
                                <TableCell align="right">RV Value</TableCell>
                                <TableCell align="right">IV-RV Difference</TableCell>
                                <TableCell>Fetch Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {implied_volatility.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell align="right">{formatPercent(row.iv_value)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.iv_percentile)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.rv_value)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.iv_rv_difference)}</TableCell>
                                    <TableCell>{formatDate(row.fetch_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default OptionsTab;
